import { useEffect, useState } from "react";
import { CalendarClock } from "lucide-react";

import { visitsApi } from "@/api/scheduling";
import { Alert } from "@/components/Alert";
import { DataTable } from "@/components/DataTable";
import { EmptyState } from "@/components/EmptyState";
import { LoadingBlock } from "@/components/LoadingBlock";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import { VisitModal } from "./VisitModal";

function visitStatusVariant(status) {
  if (status === "completed") return "default";
  if (status === "cancelled") return "secondary";
  return "outline";
}

export function ClientVisitsTab({ clientId }) {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState(null);

  const loadVisits = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await visitsApi.list({ client: clientId, page_size: 100 });
      setVisits(res.results || []);
    } catch (err) {
      setError(err.message || "Failed to load client visits.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) {
      loadVisits();
    }
  }, [clientId]);

  const columns = [
    {
      key: "datetime",
      header: "Date & Time",
      render: (v) => {
        const start = new Date(v.start_time);
        const end = new Date(v.end_time);
        return (
          <div>
            <div className="font-semibold">
              {start.toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </div>
            <div className="text-xs text-muted-foreground">
              {start.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })} –{" "}
              {end.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            </div>
          </div>
        );
      },
    },
    {
      key: "caregiver",
      header: "Caregiver",
      render: (v) =>
        v.caregiver_name || <span className="text-muted-foreground">Unassigned</span>,
    },
    {
      key: "service",
      header: "Service & Title",
      render: (v) => (
        <div>
          <div className="font-medium">{v.title || "Visit"}</div>
          <div className="text-xs text-muted-foreground">{v.service_type}</div>
        </div>
      ),
    },
    {
      key: "duration",
      header: "Duration",
      render: (v) => {
        const start = new Date(v.start_time);
        const end = new Date(v.end_time);
        const durationHrs = ((end - start) / (1000 * 60 * 60)).toFixed(1);
        return `${durationHrs} hrs`;
      },
    },
    {
      key: "status",
      header: "Status",
      render: (v) => (
        <Badge variant={visitStatusVariant(v.status)} className="capitalize">
          {v.status.replace("_", " ")}
        </Badge>
      ),
    },
  ];

  return (
    <div className="mt-6 space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-lg font-semibold tracking-tight">Visit History</h3>
        <Button
          type="button"
          onClick={() => {
            setSelectedVisit(null);
            setModalOpen(true);
          }}
        >
          + Schedule Visit
        </Button>
      </div>

      <Alert>{error}</Alert>

      {loading ? (
        <LoadingBlock label="Loading visits" />
      ) : visits.length === 0 ? (
        <EmptyState
          icon={CalendarClock}
          tone="cyan"
          title="No visits yet"
          message="No visits recorded for this client yet."
        />
      ) : (
        <DataTable
          columns={columns}
          rows={visits}
          getRowKey={(row) => row.id}
          onRowClick={(row) => {
            setSelectedVisit(row);
            setModalOpen(true);
          }}
          emptyMessage="No visits recorded for this client yet."
        />
      )}

      {modalOpen && (
        <VisitModal
          visit={selectedVisit ? { ...selectedVisit, client: clientId } : { client: clientId }}
          onClose={() => setModalOpen(false)}
          onSaved={() => {
            setModalOpen(false);
            loadVisits();
          }}
        />
      )}
    </div>
  );
}
